import { useState, useEffect, useRef, useCallback } from "react";
import ReactFlow, {
  Controls,
  Background,
  MiniMap,
  Node,
  Edge,
  NodeTypes,
  EdgeTypes,
  useNodesState,
  useEdgesState,
  BezierEdge,
  SmoothStepEdge,
  StepEdge,
  StraightEdge,
  getConnectedEdges,
  getOutgoers,
  getIncomers,
  ReactFlowInstance,
} from "reactflow";
import "reactflow/dist/style.css";
import "@/styles/diagram-tools.css";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import DiagramNode from "./DiagramNode";
import {
  transformToMindmap,
  transformToTree,
  transformToRadial,
  transformToHorizontal,
  transformToComplexMultiTier,
} from "@/lib/diagramTransformers";
import {
  Network,
  GitFork,
  Circle,
  RotateCcw,
  ArrowRight,
  Download,
  Eye,
  EyeOff,
  Zap,
  ZapOff,
  Info,
  X,
  Maximize,
  Minimize,
  RefreshCw,
  Search,
  ZoomIn,
  ZoomOut,
  Move,
  MoreHorizontal,
  MessageCircle,
  Layers,
} from "lucide-react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card";
import { clearAllDiagramCache } from "@/utils/diagramCacheService";
import { useNavigate } from "react-router-dom";
import InfoPanel from "./InfoPanel";
import { getImageWithFallback } from "@/utils/imageUtils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

// Định nghĩa các loại node tùy chỉnh
const nodeTypes: NodeTypes = {
  diagramNode: DiagramNode,
};

// Định nghĩa các loại đường kết nối
const edgeTypes: EdgeTypes = {
  default: BezierEdge,
  straight: StraightEdge,
  step: StepEdge,
  smoothstep: SmoothStepEdge,
  bezier: BezierEdge,
};

export interface DiagramData {
  id: string;
  name: string;
  description?: string;
  image?: string;
  showImage?: boolean;
  type?: string;
  level?: number;
  highlight?: boolean;
  edgeLabel?: string;
  details?: {
    content?: string;
    pali?: string;
    sanskrit?: string;
    english?: string;
    examples?: string[];
    references?: {
      title: string;
      url: string;
    }[];
    relatedConcepts?: {
      name: string;
      id: string;
    }[];
    [key: string]: any;
  };
  children?: DiagramData[];
  connections?: {
    target: string;
    type?: string;
    label?: string;
  }[];
  visualProperties?: {
    color?: string;
    borderColor?: string;
    fontSize?: number;
    shape?: "circle" | "rectangle" | "diamond" | "hexagon" | "custom";
    opacity?: number;
  };
  [key: string]: any;
}

interface DiagramViewerProps {
  data: DiagramData;
  className?: string;
  viewMode?: string;
  onNodeClick?: (node: DiagramData) => void;
  selectedNode?: DiagramData | null;
}

export const DiagramViewer = ({
  data,
  className = "",
  viewMode = "horizontal",
  onNodeClick,
  selectedNode,
}: DiagramViewerProps) => {
  const { t } = useTranslation(['catalog', 'common']);
  const [internalViewMode, setInternalViewMode] = useState<
    "mindmap" | "tree" | "radial" | "horizontal" | "complexMultiTier"
  >((viewMode as any) || "horizontal");
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [initialNodes, setInitialNodes] = useState<Node[]>([]);
  const [initialEdges, setInitialEdges] = useState<Edge[]>([]);
  const [hiddenEdges, setHiddenEdges] = useState<Set<string>>(new Set());
  const [highlightedPath, setHighlightedPath] = useState<{
    nodes: Set<string>;
    edges: Set<string>;
  }>({ nodes: new Set(), edges: new Set() });
  const [isHighlightMode, setIsHighlightMode] = useState<boolean>(true);
  const [showHighlightHint, setShowHighlightHint] = useState<boolean>(false);
  const [selectedNodeData, setSelectedNodeData] = useState<DiagramData | null>(
    null,
  );
  const [showNodeDetails, setShowNodeDetails] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const reactFlowRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance | null>(null);
  const [isMobileDevice, setIsMobileDevice] = useState<boolean>(false);
  // Touch interaction states
  const [isPinching, setIsPinching] = useState<boolean>(false);
  const [pinchDistance, setPinchDistance] = useState<number>(0);
  const [isTouchActive, setIsTouchActive] = useState<boolean>(false);
  const [touchPosition, setTouchPosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  // Mobile controls active state
  const [activeMobileControl, setActiveMobileControl] = useState<string>("pan");

  const navigate = useNavigate();

  // Check if mobile device
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobileDevice(window.innerWidth <= 768);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  // Cập nhật internalViewMode khi viewMode prop thay đổi
  useEffect(() => {
    if (viewMode) {
      setInternalViewMode(viewMode as any);
    } else {
      // Đặt chế độ xem mặc định là horizontal nếu không có chế độ xem nào được chỉ định
      setInternalViewMode("horizontal");
    }
  }, [viewMode]);

  // Chuyển đổi dữ liệu dựa trên chế độ xem
  useEffect(() => {
    let transformedData;

    switch (internalViewMode) {
      case "mindmap":
        transformedData = transformToMindmap(data);
        break;
      case "tree":
        transformedData = transformToTree(data);
        break;
      case "radial":
        transformedData = transformToRadial(data);
        break;
      case "complexMultiTier":
        transformedData = transformToComplexMultiTier(data);
        break;
      case "horizontal":
      default:
        transformedData = transformToHorizontal(data);
        break;
    }

    // Thêm chỉ báo hasDetails vào node data
    const nodesWithDetailsIndicator = transformedData.nodes.map(node => {
      // Lấy node data từ cây dữ liệu gốc
      const originalNodeData = findNodeDataById(node.id, data);
      
      // Kiểm tra xem node có details hay không
      const hasDetails = originalNodeData && 
        (originalNodeData.details?.content || 
         (originalNodeData.details?.examples && originalNodeData.details.examples.length > 0) ||
         (originalNodeData.details?.references && originalNodeData.details.references.length > 0));
      
      return {
        ...node,
        data: {
          ...node.data,
          hasDetails: !!hasDetails
        }
      };
    });

    // Đảm bảo tất cả các edge đều có type là một trong các keys của edgeTypes
    const validatedEdges = transformedData.edges.map((edge) => ({
      ...edge,
      type: Object.keys(edgeTypes).includes(edge.type as string) ? edge.type : "default"
    }));

    setNodes(nodesWithDetailsIndicator);
    setEdges(validatedEdges);
    setInitialNodes(nodesWithDetailsIndicator);
    setInitialEdges(validatedEdges);

    // Reset thông tin node được chọn khi chuyển chế độ xem
    setSelectedNodeData(null);
    setShowNodeDetails(false);
  }, [data, internalViewMode, setNodes, setEdges]);

  // Helper function to find a node data by id in the original data
  const findNodeDataById = (id: string, data: DiagramData): DiagramData | null => {
    if (data.id === id) {
      return data;
    }

    if (data.children) {
      for (const child of data.children) {
        const found = findNodeDataById(id, child);
        if (found) return found;
      }
    }

    return null;
  };

  // Lưu trạng thái màu gốc của các edge khi component được khởi tạo (vì chế độ highlight được bật mặc định)
  useEffect(() => {
    if (isHighlightMode && edges.length > 0) {
      // Lưu trạng thái màu gốc của các edge
      setEdges(
        edges.map((e) => ({
          ...e,
          style: {
            ...e.style,
            originalStroke: e.style?.stroke || "#64748b",
            transition:
              "opacity 0.3s ease, stroke 0.3s ease, stroke-width 0.3s ease",
          },
          type: e.type || "bezier",
        })),
      );

      if (!isMobileDevice) {
        toast("Thông báo", {
          description:
            "Chế độ làm nổi bật luồng đã được bật. Nhấp vào một node để xem luồng liên quan.",
        });
      }
    }
  }, [edges.length, isHighlightMode, isMobileDevice]);

  // Touch events handlers
  const handleTouchStart = useCallback((event: TouchEvent) => {
    if (event.touches.length === 2) {
      // Enable pinch zoom
      setIsPinching(true);
      const dist = Math.hypot(
        event.touches[0].clientX - event.touches[1].clientX,
        event.touches[0].clientY - event.touches[1].clientY,
      );
      setPinchDistance(dist);
    } else if (event.touches.length === 1) {
      // Single touch handling
      setIsTouchActive(true);
      setTouchPosition({
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      });
    }
  }, []);

  const handleTouchMove = useCallback(
    (event: TouchEvent) => {
      // Kiểm tra xem có đang thực hiện thao tác nào trên mobile controls hay không
      if (activeMobileControl !== "pan") {
        return; // Chỉ xử lý pan khi mode là 'pan'
      }

      if (isPinching && event.touches.length === 2) {
        // Pinch zoom
        const newDist = Math.hypot(
          event.touches[0].clientX - event.touches[1].clientX,
          event.touches[0].clientY - event.touches[1].clientY,
        );

        const delta = newDist - pinchDistance;
        if (Math.abs(delta) > 5 && reactFlowInstance) {
          const zoomIn = delta > 0;
          const step = 0.05;
          const zoom = Math.max(
            0.1,
            Math.min(2, reactFlowInstance.getZoom() + (zoomIn ? step : -step)),
          );
          reactFlowInstance.setZoom(zoom);
          setPinchDistance(newDist);
        }
      } else if (isTouchActive && event.touches.length === 1) {
        // Update touch position for UI feedback
        setTouchPosition({
          x: event.touches[0].clientX,
          y: event.touches[0].clientY,
        });
      }
    },
    [
      isPinching,
      pinchDistance,
      reactFlowInstance,
      isTouchActive,
      activeMobileControl,
    ],
  );

  const handleTouchEnd = useCallback(() => {
    setIsPinching(false);
    setIsTouchActive(false);
  }, []);

  // Add touch event listeners
  useEffect(() => {
    if (reactFlowRef.current && isMobileDevice) {
      const element = reactFlowRef.current;

      element.addEventListener("touchstart", handleTouchStart, {
        passive: true,
      });
      element.addEventListener("touchmove", handleTouchMove, { passive: true });
      element.addEventListener("touchend", handleTouchEnd);

      return () => {
        element.removeEventListener("touchstart", handleTouchStart);
        element.removeEventListener("touchmove", handleTouchMove);
        element.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [
    reactFlowRef,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    isMobileDevice,
  ]);

  // Hàm để tìm tất cả các node và edge trong một luồng
  const findPathFromNode = (
    nodeId: string,
    allNodes: Node[],
    allEdges: Edge[],
  ) => {
    const nodesToHighlight = new Set<string>([nodeId]);
    const edgesToHighlight = new Set<string>();

    // Hàm đệ quy để tìm tất cả node con và edge kết nối
    const findDescendants = (currentNodeId: string) => {
      // Tìm tất cả edge xuất phát từ node hiện tại
      const outgoingEdges = allEdges.filter(
        (edge) => edge.source === currentNodeId,
      );

      // Thêm các edge này vào danh sách cần highlight
      outgoingEdges.forEach((edge) => {
        edgesToHighlight.add(edge.id);

        // Thêm node đích vào danh sách cần highlight
        nodesToHighlight.add(edge.target);

        // Tiếp tục tìm các node con của node đích
        findDescendants(edge.target);
      });
    };

    // Hàm đệ quy để tìm tất cả node cha và edge kết nối
    const findAncestors = (currentNodeId: string) => {
      // Tìm tất cả edge đi vào node hiện tại
      const incomingEdges = allEdges.filter(
        (edge) => edge.target === currentNodeId,
      );

      // Thêm các edge này vào danh sách cần highlight
      incomingEdges.forEach((edge) => {
        edgesToHighlight.add(edge.id);

        // Thêm node nguồn vào danh sách cần highlight
        nodesToHighlight.add(edge.source);

        // Tiếp tục tìm các node cha của node nguồn
        findAncestors(edge.source);
      });
    };

    // Bắt đầu tìm từ node được click
    findDescendants(nodeId);
    findAncestors(nodeId);

    return { nodes: nodesToHighlight, edges: edgesToHighlight };
  };

  // Sửa hàm handleNodeClick để xử lý đúng khi click vào node
  const handleNodeClick = useCallback(
    (_, node: Node) => {
      // Tìm dữ liệu node từ data source
      const nodeData = findNodeDataById(node.id, data);

      if (nodeData) {
        setSelectedNodeData(nodeData);
        setShowNodeDetails(true);

        // Gọi callback nếu được cung cấp
        if (onNodeClick) {
          onNodeClick(nodeData);
        }

        // Tiếp tục xử lý highlight path nếu trong chế độ highlight
        if (isHighlightMode) {
          // Tìm đường dẫn từ node được chọn
          const result = findPathFromNode(node.id, nodes, edges);
          setHighlightedPath(result);

          // Hiển thị gợi ý nếu chưa bao giờ hiển thị
          if (!showHighlightHint) {
            setShowHighlightHint(true);
          }
        }
      }
    },
    [data, edges, findNodeDataById, findPathFromNode, isHighlightMode, nodes, onNodeClick, showHighlightHint]
  );

  // Hàm để bật/tắt chế độ highlight luồng
  const toggleHighlightMode = () => {
    const newMode = !isHighlightMode;
    setIsHighlightMode(newMode);

    if (!newMode) {
      // Nếu tắt chế độ highlight, reset lại style của tất cả node và edge
      setHighlightedPath({ nodes: new Set(), edges: new Set() });

      setNodes(
        nodes.map((n) => ({
          ...n,
          style: {
            ...n.style,
            opacity: 1,
            transition: "opacity 0.3s ease",
          },
          data: {
            ...n.data,
            highlighted: false,
          },
          className: "",
        })),
      );

      setEdges(
        edges.map((e) => ({
          ...e,
          style: {
            ...e.style,
            opacity: hiddenEdges.has(e.id) ? 0 : 1,
            stroke: e.style?.originalStroke || e.style?.stroke || "#64748b",
            strokeWidth: 2,
            transition:
              "opacity 0.3s ease, stroke 0.3s ease, stroke-width 0.3s ease",
          },
          type: Object.keys(edgeTypes).includes(e.type as string) ? e.type : "default",
          className: "",
        })),
      );

      toast("Thông báo", {
        description: "Đã tắt chế độ làm nổi bật luồng",
      });
    } else {
      // Lưu trạng thái màu gốc của các edge
      setEdges(
        edges.map((e) => ({
          ...e,
          style: {
            ...e.style,
            originalStroke: e.style?.stroke || "#64748b",
            transition:
              "opacity 0.3s ease, stroke 0.3s ease, stroke-width 0.3s ease",
          },
          type: e.type || "bezier",
        })),
      );

      toast("Thành công", {
        description: "Đã bật chế độ làm nổi bật luồng",
      });
    }
  };

  // Hàm để bật/tắt hiển thị đường kết nối khi click vào edge
  const onEdgeClick = (_: React.MouseEvent, edge: Edge) => {
    const newHiddenEdges = new Set(hiddenEdges);

    if (newHiddenEdges.has(edge.id)) {
      newHiddenEdges.delete(edge.id);
    } else {
      newHiddenEdges.add(edge.id);
    }

    setHiddenEdges(newHiddenEdges);

    // Cập nhật style của edge
    setEdges(
      edges.map((e) => {
        if (e.id === edge.id) {
          return {
            ...e,
            style: {
              ...e.style,
              opacity: newHiddenEdges.has(e.id) ? 0 : 1,
            },
            type: Object.keys(edgeTypes).includes(e.type as string) ? e.type : "default",
          };
        }
        return e;
      }),
    );
  };

  // Áp dụng trạng thái ẩn/hiện cho các đường kết nối
  useEffect(() => {
    setEdges((eds) =>
      eds.map((edge) => ({
        ...edge,
        hidden: hiddenEdges.has(edge.id),
        style: {
          ...edge.style,
          opacity: hiddenEdges.has(edge.id) ? 0 : 1,
        },
      })),
    );
  }, [hiddenEdges, setEdges]);

  // Hàm xuất sơ đồ dưới dạng PDF
  const exportToPdf = useCallback(async () => {
    if (!reactFlowRef.current) return;

    try {
      // Hiển thị thông báo đang xuất PDF
      const notification = document.createElement("div");
      notification.style.position = "absolute";
      notification.style.top = "50%";
      notification.style.left = "50%";
      notification.style.transform = "translate(-50%, -50%)";
      notification.style.padding = "10px 20px";
      notification.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
      notification.style.color = "white";
      notification.style.borderRadius = "5px";
      notification.style.zIndex = "1000";
      notification.textContent = t("diagram.exporting") || "Đang xuất PDF...";
      document.body.appendChild(notification);

      // Tạo canvas từ ReactFlow
      const canvas = await html2canvas(reactFlowRef.current, {
        backgroundColor: "#ffffff",
        scale: 2, // Tăng độ phân giải
      });

      // Chuyển canvas thành hình ảnh
      const imgData = canvas.toDataURL("image/png");

      // Tạo PDF với kích thước A4
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
      });

      // Tính toán kích thước để giữ tỷ lệ khung hình
      const imgWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Thêm hình ảnh vào PDF
      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);

      // Tải xuống PDF
      pdf.save("diagram.pdf");

      // Xóa thông báo
      document.body.removeChild(notification);

      // Thông báo thành công
      toast("Thành công", {
        description: "Đã xuất sơ đồ thành công",
      });
    } catch (error) {
      console.error("Error exporting PDF:", error);
      alert(t("diagram.exportError") || "Có lỗi khi xuất PDF");
    }
  }, [t]);

  // Hàm bật/tắt tất cả các đường kết nối
  const toggleAllEdges = useCallback(() => {
    if (hiddenEdges.size > 0) {
      // Nếu có edge đang ẩn, hiện tất cả
      setHiddenEdges(new Set());
    } else {
      // Nếu tất cả đang hiện, ẩn tất cả
      setHiddenEdges(new Set(edges.map((edge) => edge.id)));
    }
  }, [edges, hiddenEdges]);

  // Render InfoPanel trong Dialog
  const NodeDetailsPanel = () => {
    if (!selectedNodeData) return null;

    return (
      <Dialog open={showNodeDetails} onOpenChange={setShowNodeDetails}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-auto p-0">
          <DialogHeader>
            <DialogTitle className="sr-only">{selectedNodeData.name}</DialogTitle>
          </DialogHeader>
          <InfoPanel 
            data={selectedNodeData} 
            hideTitle={false} 
            className="border-0 shadow-none"
          />
        </DialogContent>
      </Dialog>
    );
  };

  // Hàm để bật/tắt chế độ toàn màn hình
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      if (containerRef.current?.requestFullscreen) {
        containerRef.current
          .requestFullscreen()
          .then(() => {
            setIsFullscreen(true);

            // Try to lock to landscape on mobile if supported
            if (isMobileDevice && window.screen?.orientation) {
              try {
                window.screen.orientation
                  .lock("landscape")
                  .catch((err) =>
                    console.log("Could not lock screen orientation: ", err),
                  );
              } catch (e) {
                console.log("Screen orientation API not supported");
              }
            }
          })
          .catch((err) => {
            console.error("Error attempting to enable full-screen mode: ", err);
          });
      }
    } else {
      if (document.exitFullscreen) {
        document
          .exitFullscreen()
          .then(() => {
            setIsFullscreen(false);

            // Unlock orientation when exiting fullscreen
            if (isMobileDevice && window.screen?.orientation) {
              try {
                window.screen.orientation.unlock();
              } catch (e) {
                console.log("Screen orientation API not supported");
              }
            }
          })
          .catch((err) => {
            console.error("Error attempting to exit full-screen mode: ", err);
          });
      }
    }
  };

  // Change mobile control mode
  const setMobileControlMode = useCallback(
    (mode: string) => {
      setActiveMobileControl(mode);

      if (mode === "zoom-in" && reactFlowInstance) {
        reactFlowInstance.zoomIn({ duration: 300 });
      } else if (mode === "zoom-out" && reactFlowInstance) {
        reactFlowInstance.zoomOut({ duration: 300 });
      } else if (mode === "reset" && reactFlowInstance) {
        reactFlowInstance.fitView({ duration: 500, padding: 0.2 });
      } else if (mode === "fullscreen") {
        toggleFullscreen();
      }
    },
    [reactFlowInstance],
  );

  // Xử lý zoom và pan
  const handleZoom = useCallback(
    (event: { delta: number }) => {
      if (reactFlowInstance) {
        reactFlowInstance.zoomTo(
          Math.max(
            0.1,
            Math.min(2, reactFlowInstance.getZoom() + event.delta * 0.05),
          ),
        );
      }
    },
    [reactFlowInstance],
  );

  // Thêm useEffect để kết nối Panzoom với ReactFlow trên mobile
  useEffect(() => {
    if (isMobileDevice && reactFlowInstance) {
      // Đặt lại viewport controls của ReactFlow để tối ưu cho mobile
      reactFlowInstance.setViewport({ x: 0, y: 0, zoom: 0.8 }, { duration: 0 });
    }
  }, [isMobileDevice, reactFlowInstance]);

  // Render các nút control cho chế độ xem
  const renderViewControls = () => {
    return (
      <div className="diagram-view-controls">
        <Button
          variant={internalViewMode === "horizontal" ? "default" : "outline"}
          size="sm"
          onClick={() => setInternalViewMode("horizontal")}
          title={t("diagram.Horizontal Layout")}
        >
          <ArrowRight className="w-4 h-4" />
        </Button>
        <Button
          variant={internalViewMode === "mindmap" ? "default" : "outline"}
          size="sm"
          onClick={() => setInternalViewMode("mindmap")}
          title={t("diagram.Mind Map")}
        >
          <Network className="w-4 h-4" />
        </Button>
        <Button
          variant={internalViewMode === "tree" ? "default" : "outline"}
          size="sm"
          onClick={() => setInternalViewMode("tree")}
          title={t("diagram.Tree Layout")}
        >
          <GitFork className="w-4 h-4" />
        </Button>
        <Button
          variant={internalViewMode === "radial" ? "default" : "outline"}
          size="sm"
          onClick={() => setInternalViewMode("radial")}
          title={t("diagram.Radial Layout")}
        >
          <Circle className="w-4 h-4" />
        </Button>
        <Button
          variant={
            internalViewMode === "complexMultiTier" ? "default" : "outline"
          }
          size="sm"
          onClick={() => setInternalViewMode("complexMultiTier")}
          title={t("diagram.viewModes.complexMultiTier")}
        >
          <Layers className="w-4 h-4" />
        </Button>
      </div>
    );
  };

  // Render các điều khiển cho mobile
  const renderMobileControls = () => {
    return (
      <div className="mobile-diagram-controls fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur border-t p-2 flex justify-around items-center z-50">
        <button
          className={`mobile-diagram-control-btn flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${
            activeMobileControl === "pan" ? "bg-primary/10 text-primary" : "hover:bg-accent"
          }`}
          onClick={() => setMobileControlMode("pan")}
        >
          <Move size={20} />
          <span className="text-xs">{t('diagram.controls.pan')}</span>
        </button>
        <button
          className={`mobile-diagram-control-btn flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${
            activeMobileControl === "zoom-in" ? "bg-primary/10 text-primary" : "hover:bg-accent"
          }`}
          onClick={() => setMobileControlMode("zoom-in")}
        >
          <ZoomIn size={20} />
          <span className="text-xs">{t('diagram.controls.zoomIn')}</span>
        </button>
        <button
          className={`mobile-diagram-control-btn flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${
            activeMobileControl === "zoom-out" ? "bg-primary/10 text-primary" : "hover:bg-accent"
          }`}
          onClick={() => setMobileControlMode("zoom-out")}
        >
          <ZoomOut size={20} />
          <span className="text-xs">{t('diagram.controls.zoomOut')}</span>
        </button>
        <button
          className={`mobile-diagram-control-btn flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${
            activeMobileControl === "reset" ? "bg-primary/10 text-primary" : "hover:bg-accent"
          }`}
          onClick={() => setMobileControlMode("reset")}
        >
          <RefreshCw size={20} />
          <span className="text-xs">{t('diagram.controls.reset')}</span>
        </button>
        <button
          className={`mobile-diagram-control-btn flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${
            activeMobileControl === "fullscreen" ? "bg-primary/10 text-primary" : "hover:bg-accent"
          }`}
          onClick={() => setMobileControlMode("fullscreen")}
        >
          {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
          <span className="text-xs">
            {isFullscreen ? t('diagram.controls.exitFullscreen') : t('diagram.controls.fullscreen')}
          </span>
        </button>
      </div>
    );
  };

  return (
    <div ref={containerRef} className={`diagram-container ${className}`}>
      <div className="diagram-tools flex items-center gap-2 flex-wrap mb-2">
        {renderViewControls()}

        {!isMobileDevice && (
          <div className="action-buttons flex gap-1 ml-auto">
            <Button
              size="sm"
              variant="outline"
              onClick={toggleHighlightMode}
              className="action-button"
              title={
                isHighlightMode
                  ? t("diagram.disable_highlight")
                  : t("diagram.enable_highlight")
              }
            >
              {isHighlightMode ? (
                <>
                  <ZapOff className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">
                    {t("diagram.disable_highlight")}
                  </span>
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">
                    {t("diagram.enable_highlight")}
                  </span>
                </>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={toggleAllEdges}
              className="action-button"
              title={
                hiddenEdges.size > 0
                  ? t("diagram.showAllEdges") || "Hiện tất cả kết nối"
                  : t("diagram.hideAllEdges") || "Ẩn tất cả kết nối"
              }
            >
              {hiddenEdges.size > 0 ? (
                <Eye className="h-4 w-4 mr-1" />
              ) : (
                <EyeOff className="h-4 w-4 mr-1" />
              )}
              <span className="hidden sm:inline">
                {hiddenEdges.size > 0
                  ? t("diagram.showAllEdges") || "Hiện kết nối"
                  : t("diagram.hideAllEdges") || "Ẩn kết nối"}
              </span>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={exportToPdf}
              className="action-button"
              title={t("diagram.export_pdf")}
            >
              <Download className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">PDF</span>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={toggleFullscreen}
              className="action-button"
              title={
                isFullscreen
                  ? t("diagram.exit_fullscreen")
                  : t("diagram.fullscreen")
              }
            >
              {isFullscreen ? (
                <Minimize className="h-4 w-4" />
              ) : (
                <Maximize className="h-4 w-4" />
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Touch handle indicator for mobile */}
      {isTouchActive && isMobileDevice && (
        <div
          className="touch-handle"
          style={{
            left: touchPosition.x,
            top: touchPosition.y,
            transform: "translate(-50%, -50%)",
          }}
        />
      )}

      <div
        ref={reactFlowRef}
        style={{
          height: 600,
          width: "100%",
        }}
        className={`diagram-flow relative md:border md:rounded-md ${isFullscreen ? "fullscreen" : ""}`}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges.filter((edge) => !hiddenEdges.has(edge.id))}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          proOptions={{ hideAttribution: true }}
          onInit={setReactFlowInstance}
          onNodeClick={handleNodeClick}
          onEdgeClick={onEdgeClick}
          minZoom={0.1}
          maxZoom={4}
          defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
          zoomOnScroll={!isMobileDevice}
          zoomOnPinch={true}
          panOnScroll={false}
          panOnDrag={true}
          selectionOnDrag={false}
          snapToGrid={false}
          snapGrid={[15, 15]}
          attributionPosition="bottom-left"
          elementsSelectable={true}
          nodesDraggable={true}
          nodesConnectable={false}
          className=""
          translateExtent={[
            [-5000, -5000],
            [10000, 10000],
          ]}
          nodeExtent={[
            [-5000, -5000],
            [10000, 10000],
          ]}
        >
          <Controls
            position={isMobileDevice ? "bottom-right" : "bottom-left"}
            showInteractive={false}
            className={isMobileDevice ? "mobile-controls" : ""}
          />
          <Background color="#aaa" gap={16} />
          <MiniMap
            nodeStrokeWidth={3}
            zoomable
            pannable
            nodeColor={(node) => {
              return highlightedPath.nodes.has(node.id)
                ? "#ff4d94"
                : node.data?.color || "#ffb56b";
            }}
            className={isMobileDevice ? "hidden" : ""}
          />
        </ReactFlow>
      </div>

      {/* Mobile floating action button - chỉ hiển thị khi node có detail */}
      {isMobileDevice && selectedNodeData && !showNodeDetails && 
       (selectedNodeData.details?.content || 
        (selectedNodeData.details?.examples && selectedNodeData.details.examples.length > 0) ||
        (selectedNodeData.details?.references && selectedNodeData.details.references.length > 0 && 
          selectedNodeData.details.references.some(ref => ref.title && ref.title.trim() !== '')) ||
        (selectedNodeData.children && selectedNodeData.children.length > 0)) && (
        <div 
          onClick={() => setShowNodeDetails(true)}
          className="fixed bottom-20 right-4 z-50 rounded-full bg-primary text-primary-foreground shadow-lg animate-in fade-in slide-in-from-bottom-5 p-3 cursor-pointer"
        >
          <Info className="h-6 w-6" />
        </div>
      )}

      {/* Mobile controls bar */}
      {isMobileDevice && renderMobileControls()}

      {/* Hiển thị NodeDetailsPanel bên ngoài ReactFlow container */}
      <NodeDetailsPanel />
    </div>
  );
};
